import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  CreateMappingPayload,
  CreateMappingsPayload,
  Mapping,
  MappingListParams,
  MappingPayload,
  BalanceAdjustmentResult,
  BalanceApproval,
  CreateProviderPayload,
  Provider,
  ProviderAvailability,
  ProviderAvailabilityItem,
  ProviderAvailabilityParams,
  ProviderListParams,
  ProviderStockCheck,
  ProviderStockSync,
} from '../types/provider.types';

/**
 * Provider and mapping HTTP calls — all against the Admin API, which checks
 * permissions and scope, never returns provider credentials, omits costs
 * without catalogue.cost.read, and audits every change.
 */

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

function toPaged<T>(page: AdminPage<T>): PagedResponse<T> {
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

// ---------------------------------------------------------------- providers

export async function getProviders(
  params: ProviderListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<Provider>> {
  return toPaged(await http.get<AdminPage<Provider>>('/admin/providers', { params, signal }));
}

export async function getProvider(providerId: number, signal?: AbortSignal): Promise<Provider> {
  return http.get<Provider>(`/admin/providers/${providerId}`, { signal });
}

/**
 * Where checkout buys from this provider, where it is only a fallback and
 * where it cannot be used, per country in scope (catalogue.read). No costs.
 */
export async function getProviderAvailability(
  providerId: number,
  params: ProviderAvailabilityParams,
  signal?: AbortSignal,
): Promise<ProviderAvailability> {
  const data = await http.get<{
    providerId: number;
    countries: ProviderAvailability['countries'];
    items: AdminPage<ProviderAvailabilityItem>;
  }>(`/admin/providers/${providerId}/availability`, { params, signal });
  return { ...data, items: toPaged(data.items) };
}

/**
 * Creates a provider, inactive and with a zero balance; returns its id. The
 * Admin API refuses a name in use with 409 `PROVIDER_NAME_TAKEN` and an unknown
 * currency with 400 `CURRENCY_NOT_FOUND`, both as field errors.
 */
export async function createProvider(payload: CreateProviderPayload): Promise<number> {
  return http.post<number>('/admin/providers', payload);
}

export async function setProviderStatus(
  providerId: number,
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/providers/${providerId}/status`, {
    isActive,
    reason,
  });
  return result.changed;
}

/**
 * Deletes a provider nothing uses: no mapping (active or not), no order, a zero
 * balance and no pending balance adjustment. The Admin API re-checks under a
 * lock and refuses the rest with 409 `PROVIDER_IN_USE` / `PROVIDER_HAS_BALANCE`.
 */
export async function deleteProvider(providerId: number, reason: string | null): Promise<void> {
  await http.delete<number>(`/admin/providers/${providerId}`, { data: { reason } });
}

export async function updateProviderBalanceSettings(
  providerId: number,
  settings: { isBalanceConsidered: boolean; lowBalanceThreshold: number | null },
): Promise<void> {
  await http.put<number>(`/admin/providers/${providerId}/balance-settings`, settings);
}

/**
 * Positive adds, negative deducts. Above the configured approval threshold the
 * backend only records a request (applied: false) that another staff member
 * with providers.balance.approve must approve.
 */
export async function adjustProviderBalance(
  providerId: number,
  amount: number,
  reason: string,
): Promise<BalanceAdjustmentResult> {
  return http.post<BalanceAdjustmentResult>(`/admin/providers/${providerId}/balance-adjustments`, {
    amount,
    reason,
  });
}

// ----------------------------------------------------------------- mappings

export async function getMappings(
  params: MappingListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<Mapping>> {
  return toPaged(await http.get<AdminPage<Mapping>>('/admin/mappings', { params, signal }));
}

/** Returns the new mapping's id. */
export async function createMapping(payload: CreateMappingPayload): Promise<number> {
  return http.post<number>('/admin/mappings', payload);
}

/** Maps several providers at once; returns the new mapping ids. */
export async function createMappings(payload: CreateMappingsPayload): Promise<number[]> {
  return http.post<number[]>('/admin/mappings/batch', payload);
}

export async function updateMapping(mappingId: number, payload: MappingPayload): Promise<void> {
  await http.put<number>(`/admin/mappings/${mappingId}`, payload);
}

export async function setMappingStatus(
  mappingId: number,
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/mappings/${mappingId}/status`, {
    isActive,
    reason,
  });
  return result.changed;
}

// ------------------------------------------------------ balance approvals

export async function getBalanceApprovals(
  providerId: number,
  status: 'Pending' | undefined,
  signal?: AbortSignal,
): Promise<BalanceApproval[]> {
  const page = await http.get<{ items: BalanceApproval[] }>('/admin/providers/balance-approvals', {
    params: { providerId, status, page: 1, pageSize: 50 },
    signal,
  });
  return page.items;
}

export async function decideBalanceApproval(
  approvalId: number,
  decision: 'approve' | 'reject' | 'cancel',
  reason: string | null,
): Promise<void> {
  await http.post<number>(`/admin/providers/balance-approvals/${approvalId}/${decision}`, {
    reason,
  });
}

/** The provider list as Excel (reports.export + providers.read). Never includes credentials. */
export async function exportProviders(params: Omit<ProviderListParams, 'page' | 'pageSize'>) {
  const { blob, fileName } = await http.download('/admin/providers/export', {
    params: { ...params, format: 'xlsx' },
  });
  return { blob, fileName: fileName ?? 'providers.xlsx' };
}

// ---------------------------------------------------------------- stock sync

/** The providers whose stock the fulfilment worker synchronises (providers.read). */
export async function getProviderStockSyncs(signal?: AbortSignal): Promise<ProviderStockSync[]> {
  return http.get<ProviderStockSync[]>('/admin/providers/stock-sync', { signal });
}

/**
 * Asks the worker to synchronise the provider's stock now (providers.manage).
 * The worker runs it within about a minute; the request never calls the provider.
 */
export async function requestProviderStockRefresh(providerId: number): Promise<void> {
  await http.post<void>(`/admin/providers/${providerId}/stock-sync/refresh`);
}

/**
 * Reads one item's stock from the provider now — one availability call — and
 * stores it on the item's mappings (providers.manage). Only LikeCard has a
 * stock read.
 */
export async function checkProviderItemStock(
  providerId: number,
  denominationId: number,
): Promise<ProviderStockCheck> {
  return http.post<ProviderStockCheck>(
    `/admin/providers/${providerId}/denominations/${denominationId}/stock-check`,
  );
}

/**
 * Reads the provider's balance from its balance API now and stores it, as the
 * worker's balance sync does every two hours (providers.read). Answers the
 * provider as stored: the new balance, or the read's error with the last one kept.
 */
export async function refreshProviderBalance(providerId: number): Promise<Provider> {
  return http.post<Provider>(`/admin/providers/${providerId}/provider-balance/refresh`);
}
